const feeProductType = 'mws_fee_generated';
const feeChangeStorageKey = 'cart-fee-change-animation';
const pendingFeeStorageKey = 'cart-fee-pending-selection-v4';
let upchargeRefreshPending = false;
let feeReconciliationPending = false;
const marketFeePriceRequests = new Map();

function getFeeTranslations() {
  const recap = document.querySelector('.cart-recap');

  return {
    description: recap?.dataset.feeDescription || '',
  };
}

function rememberFeeChange() {
  try {
    window.sessionStorage.setItem(feeChangeStorageKey, Date.now().toString());
  } catch (error) {
    // The cart update must still work when storage is unavailable.
  }
}

function animateFeeChange() {
  try {
    const changedAt = Number(window.sessionStorage.getItem(feeChangeStorageKey));
    window.sessionStorage.removeItem(feeChangeStorageKey);

    if (!changedAt || Date.now() - changedAt > 10000) return;

    document.querySelector('.cart-recap')?.classList.add('cart-recap--fee-changed');
  } catch (error) {
    // Animation is an optional enhancement.
  }
}

function formatWholeFeeAmount(sign, units, cents, locale, suffix, replacePeriodSeparator = false, rate = 1) {
  const baseAmount = Number(units.replace(/[.\s\u00a0]/g, '')) + Number(cents || '0') / 100;
  const amount = baseAmount * rate;
  let rounded = Math.round(amount).toLocaleString(locale, { maximumFractionDigits: 0 }).replace(/\u00a0/g, ' ');

  if (replacePeriodSeparator) {
    rounded = rounded.replace(/\./g, ' ');
  }

  return `${sign.replace(/\s/g, '')}${rounded}${suffix}`;
}

function getMarketFeeConversionRate() {
  const recap = document.querySelector('.cart-recap');
  const countryCode = recap?.dataset.countryCode;
  const currencyCode = recap?.dataset.currencyCode;
  const shopCurrencyCode = recap?.dataset.shopCurrencyCode;
  const requiresConversion =
    shopCurrencyCode === 'PLN' &&
    ((countryCode === 'CZ' && currencyCode === 'CZK') ||
      (countryCode === 'SK' && currencyCode === 'EUR') ||
      (countryCode === 'HU' && currencyCode === 'HUF'));

  if (!requiresConversion) return null;

  const rate = Number(window.Shopify?.currency?.rate);
  return Number.isFinite(rate) && rate > 0 ? rate : null;
}

function normalizeMarketFeePrice(value, conversionRate = 1) {
  const recap = document.querySelector('.cart-recap');
  const countryCode = recap?.dataset.countryCode;
  const currencyCode = recap?.dataset.currencyCode;

  if (countryCode === 'CZ' && currencyCode === 'CZK') {
    return value.replace(
      /([+-]?\s*)(\d[\d.\s\u00a0]*)(?:,(\d{2}))?\s*(?:K\u010d(?:\s*CZK)?|CZK)/gu,
      (_match, sign, units, cents = '0') =>
        formatWholeFeeAmount(sign, units, cents, 'cs-CZ', ' K\u010d', false, conversionRate)
    );
  }

  if (countryCode === 'SK' && currencyCode === 'EUR') {
    return value.replace(
      /([+-]?\s*)(?:€\s*)?(\d[\d.\s\u00a0]*)(?:,(\d{2}))?\s*(?:€|EUR)?/gu,
      (_match, sign, units, cents = '0') =>
        formatWholeFeeAmount(sign, units, cents, 'sk-SK', ' €', false, conversionRate)
    );
  }

  if (countryCode === 'HU' && currencyCode === 'HUF') {
    return value.replace(
      /([+-]?\s*)(\d[\d.\s\u00a0]*)(?:,(\d{2}))?\s*(?:Ft|HUF)(?:\s*HUF)?/gu,
      (_match, sign, units, cents = '0') =>
        formatWholeFeeAmount(sign, units, cents, 'hu-HU', ' Ft', false, conversionRate)
    );
  }

  if (countryCode === 'RO' && currencyCode === 'RON') {
    return value.replace(
      /([+-]?\s*)(\d[\d.\s\u00a0]*)(?:,(\d{2}))?\s*(?:lei|RON)(?:\s*RON)?/giu,
      (_match, sign, units, cents = '0') => formatWholeFeeAmount(sign, units, cents, 'ro-RO', ' lei', true)
    );
  }

  return value;
}

function getUpchargeFeesConfig() {
  if (Array.isArray(window.mwFeesConfig?.fees)) return window.mwFeesConfig.fees;

  const configElement = document.getElementById?.('mwFeesConfigData');

  if (!configElement?.textContent) return [];

  try {
    const config = JSON.parse(configElement.textContent);
    return Array.isArray(config?.fees) ? config.fees : [];
  } catch (error) {
    return [];
  }
}

function getUpchargeFeeVariant(price) {
  const feeId = price.closest?.('.mw-fee--type-cart')?.dataset.feeId;

  if (!feeId) return null;

  const fee = getUpchargeFeesConfig().find(({ product_id: productId }) => String(productId) === String(feeId));
  const option = fee?.options?.find(({ variant_id: variantId }) => variantId);

  if (!fee?.product_handle || !option?.variant_id) return null;

  return {
    handle: fee.product_handle,
    variantId: String(option.variant_id),
  };
}

function formatMarketFeePrice(priceInMinorUnits) {
  const recap = document.querySelector('.cart-recap');
  const countryCode = recap?.dataset.countryCode;
  const currencyCode = recap?.dataset.currencyCode;

  if (priceInMinorUnits === null || priceInMinorUnits === undefined || priceInMinorUnits === '') return null;

  const amount = Number(priceInMinorUnits) / 100;

  if (!Number.isFinite(amount)) return null;

  if (countryCode === 'CZ' && currencyCode === 'CZK') {
    return `+${Math.round(amount).toLocaleString('cs-CZ', { maximumFractionDigits: 0 }).replace(/\u00a0/g, ' ')} K\u010d`;
  }

  if (countryCode === 'SK' && currencyCode === 'EUR') {
    return `+${Math.round(amount).toLocaleString('sk-SK', { maximumFractionDigits: 0 }).replace(/\u00a0/g, ' ')} \u20ac`;
  }

  if (countryCode === 'HU' && currencyCode === 'HUF') {
    return `+${Math.round(amount).toLocaleString('hu-HU', { maximumFractionDigits: 0 }).replace(/\u00a0/g, ' ')} Ft`;
  }

  if (countryCode === 'RO' && currencyCode === 'RON') {
    const formattedAmount = Math.round(amount)
      .toLocaleString('ro-RO', { maximumFractionDigits: 0 })
      .replace(/\u00a0/g, ' ')
      .replace(/\./g, ' ');

    return `+${formattedAmount} lei`;
  }

  return null;
}

function fetchMarketFeePrice({ handle, variantId }) {
  const currencyCode = document.querySelector('.cart-recap')?.dataset.currencyCode || '';
  const cacheKey = `${currencyCode}:${handle}:${variantId}`;

  if (marketFeePriceRequests.has(cacheKey)) return marketFeePriceRequests.get(cacheKey);

  const request = (async () => {
    const shopifyRoot = window.Shopify?.routes?.root || '/';
    const normalizedRoot = shopifyRoot.endsWith('/') ? shopifyRoot : `${shopifyRoot}/`;
    const response = await window.fetch(`${normalizedRoot}products/${encodeURIComponent(handle)}.js`, {
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) throw new Error(`Unable to load the market fee price (${response.status}).`);

    const product = await response.json();
    const variant = product?.variants?.find(({ id }) => String(id) === variantId);
    const price = Number(variant?.price);

    return Number.isFinite(price) ? price : null;
  })().catch(() => {
    marketFeePriceRequests.delete(cacheKey);
    return null;
  });

  marketFeePriceRequests.set(cacheKey, request);
  return request;
}

async function applyMarketFeePrice(price) {
  if (price.dataset.cartFeeMarketPrice === price.textContent.trim()) return;

  const feeVariant = getUpchargeFeeVariant(price);

  if (!feeVariant) return;

  const priceInMinorUnits = await fetchMarketFeePrice(feeVariant);
  const marketPrice = formatMarketFeePrice(priceInMinorUnits);
  const currentFeeVariant = getUpchargeFeeVariant(price);

  if (!marketPrice || currentFeeVariant?.variantId !== feeVariant.variantId) return;

  price.dataset.cartFeeMarketPrice = marketPrice;
  price.textContent = marketPrice;
}

function normalizeUpchargePrices(root = document) {
  const priceSelector = '.cart-recap__order-value .mw-fee--type-cart .cart-recap__fee-price';
  const prices = [...(root.querySelectorAll?.(priceSelector) || [])];

  if (root.matches?.(priceSelector)) prices.unshift(root);

  prices.forEach((price) => {
    const conversionRate = getMarketFeeConversionRate();
    const currentPrice = price.textContent.trim();
    const hasMarketPrice = price.dataset.cartFeeMarketPrice === currentPrice;
    const isAlreadyConverted =
      conversionRate !== null && price.dataset.cartFeeConvertedPrice === currentPrice;
    const normalizedPrice = hasMarketPrice
      ? price.textContent
      : normalizeMarketFeePrice(price.textContent, isAlreadyConverted ? 1 : conversionRate || 1);

    if (conversionRate !== null) price.dataset.cartFeeConvertedPrice = normalizedPrice.trim();
    if (normalizedPrice !== price.textContent) price.textContent = normalizedPrice;

    applyMarketFeePrice(price);
  });
}

function parseUpchargeTitle(value) {
  const match = value
    .trim()
    .match(/^(.*?)\s*[-\u2013\u2014]\s*(\+?\s*(?:[^\d\s.,]+\s*)?\d[\d\s.,]*(?:\s*[^\d\s.,]*)?)$/u);

  if (!match) return null;

  return {
    title: match[1].trim(),
    price: `+${match[2].replace(/^\+\s*/, '').trim()}`,
  };
}

function formatUpchargeRows(root = document) {
  const titleSelector =
    '.cart-recap__order-value .mw-fee--type-cart :is(.mw-fee__control-title, .mw-text--style-strong):not([data-cart-fee-formatted])';
  const titles = [...(root.querySelectorAll?.(titleSelector) || [])];

  if (root.matches?.(titleSelector)) titles.unshift(root);

  titles.forEach((title) => {
    const parsedTitle = parseUpchargeTitle(title.textContent);

    if (!parsedTitle) return;

    const label = title.closest('.mw-label');
    if (!label || label.querySelector('.cart-recap__fee-price')) return;

    title.textContent = parsedTitle.title;
    title.dataset.cartFeeFormatted = 'true';

    const price = document.createElement('span');
    price.className = 'cart-recap__fee-price';
    price.textContent = parsedTitle.price;
    label.append(price);
  });
}

function enhanceUpchargeCards(root = document) {
  const labelSelector =
    '.cart-recap__order-value .mw-fee--type-cart .mw-label:not([data-cart-fee-enhanced])';
  const labels = [...(root.querySelectorAll?.(labelSelector) || [])];

  if (root.matches?.(labelSelector)) labels.unshift(root);

  labels.forEach((label) => {
    const translations = getFeeTranslations();
    const checkbox = label.querySelector('input[type="checkbox"]');
    const title = label.querySelector('.mw-fee__control-title, .mw-text--style-strong');
    const card = label.closest('.mw-fee--type-cart');

    if (!checkbox || !title || !card) return;

    let copy = label.querySelector('.mw-fee__control-container');

    if (!copy) {
      copy = document.createElement('span');
      title.before(copy);
      copy.append(title);
    }

    copy.classList.add('cart-recap__fee-copy');

    const description = document.createElement('span');
    description.className = 'cart-recap__fee-description';
    description.textContent = translations.description;

    copy.append(description);
    label.dataset.cartFeeEnhanced = 'true';

    const updateCardState = () => {
      const selected = checkbox.checked;
      card.classList.toggle('cart-recap__fee--selected', selected);
    };

    checkbox.addEventListener('change', updateCardState);
    updateCardState();
  });

  reconcileVisibleChangedFee();
  restorePendingFeeSelection();
}

function normalizeTitle(value) {
  return value.trim().replace(/\s+/g, ' ').toLocaleLowerCase();
}

function parseMoneyToCents(value) {
  const price = value.replace(/[^\d,.-]/g, '');
  const decimalMatch = price.match(/[,.](\d{2})$/);

  if (!decimalMatch) return Number(price.replace(/[^\d-]/g, '')) * 100;

  const integerPart = price.slice(0, -decimalMatch[0].length).replace(/[^\d-]/g, '');
  return Number(integerPart) * 100 + Number(decimalMatch[1]);
}

function getUpchargeDescriptor(checkbox) {
  const card = checkbox?.closest('.mw-fee--type-cart');
  const title = card?.querySelector('.mw-fee__control-title, .mw-text--style-strong')?.textContent || '';
  const price = card?.querySelector('.cart-recap__fee-price')?.textContent || '';

  if (!title) return null;

  return {
    amount: price ? parseMoneyToCents(price) : null,
    title: normalizeTitle(title),
  };
}

function cartItemMatchesFee(item, descriptor) {
  if (item.product_type !== feeProductType) return false;

  const title = normalizeTitle(item.product_title || item.title || '');
  return title === descriptor.title;
}

function setCheckoutLocked(locked) {
  const cartForm = document.querySelector('.cart-page');
  const checkoutButtons = cartForm?.querySelectorAll('[name="checkout"]') || [];
  const acceleratedCheckout = cartForm?.querySelector('.additional-checkout-buttons');

  cartForm?.toggleAttribute('aria-busy', locked);
  checkoutButtons.forEach((button) => {
    if (locked) {
      button.dataset.cartFeeWasDisabled = button.disabled.toString();
      button.disabled = true;
    } else if (button.dataset.cartFeeWasDisabled) {
      button.disabled = button.dataset.cartFeeWasDisabled === 'true';
      delete button.dataset.cartFeeWasDisabled;
    }
  });

  if (acceleratedCheckout) {
    if (locked) {
      acceleratedCheckout.dataset.cartFeeWasInert = acceleratedCheckout.inert.toString();
      acceleratedCheckout.inert = true;
    } else if (acceleratedCheckout.dataset.cartFeeWasInert) {
      acceleratedCheckout.inert = acceleratedCheckout.dataset.cartFeeWasInert === 'true';
      delete acceleratedCheckout.dataset.cartFeeWasInert;
    }
  }
}

function showReconciliationError(fees) {
  fees.forEach((fee) => {
    const error = fee.querySelector('[role="alert"]');
    if (error) error.hidden = false;
  });
}

async function waitForFee(descriptor, shouldExist, attempts = 40) {
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    await delay(250);

    const cart = await fetchCurrentCart();
    const hasFee = cart.items.some((item) => cartItemMatchesFee(item, descriptor));

    if (hasFee === shouldExist) return cart;
  }

  throw new Error('The cart fee update timed out.');
}

function getStoredValue(key) {
  try {
    return window.sessionStorage.getItem(key) || '';
  } catch (error) {
    return '';
  }
}

function setStoredValue(key, value) {
  try {
    if (value) window.sessionStorage.setItem(key, value);
    else window.sessionStorage.removeItem(key);
  } catch (error) {
    // Cart actions must still work when storage is unavailable.
  }
}

async function reconcileVisibleChangedFee() {
  if (feeReconciliationPending || upchargeRefreshPending || getStoredValue(pendingFeeStorageKey)) return;

  const selectedFees = [...document.querySelectorAll('cart-fee-control[data-fee-title][data-fee-price]')];
  const replacementCheckboxes = [
    ...document.querySelectorAll('.mw-fee--type-cart input[type="checkbox"]:not(:checked)'),
  ];
  const conflict = selectedFees
    .map((fee) => {
      const selectedTitle = normalizeTitle(fee.dataset.feeTitle || '');
      const currentAmount = Number(fee.dataset.feePrice);
      const checkbox = replacementCheckboxes.find((input) => {
        const descriptor = getUpchargeDescriptor(input);
        const isOnlyFeeOption = selectedFees.length === 1 && replacementCheckboxes.length === 1;
        const isSameService = descriptor?.title === selectedTitle || isOnlyFeeOption;
        return descriptor && isSameService && descriptor.amount !== null && descriptor.amount !== currentAmount;
      });

      const replacement = getUpchargeDescriptor(checkbox);
      return checkbox ? { fee, pendingTitle: replacement.title, selectedTitle } : null;
    })
    .find(Boolean);

  if (!conflict) return;

  feeReconciliationPending = true;
  setCheckoutLocked(true);
  document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
  setStoredValue(pendingFeeStorageKey, conflict.pendingTitle);

  try {
    const cart = await fetchCurrentCart();
    const descriptor = { title: conflict.selectedTitle };
    const updates = cart.items.reduce((result, item) => {
      if (item.key === conflict.fee.dataset.lineKey || cartItemMatchesFee(item, descriptor)) result[item.key] = 0;
      return result;
    }, {});

    if (Object.keys(updates).length === 0) throw new Error('The stale cart fee is missing.');

    await changeCart('cart/update.js', { updates }, 'cart-fee:remove-visible-stale');
    window.location.reload();
  } catch (error) {
    setStoredValue(pendingFeeStorageKey, '');
    feeReconciliationPending = false;
    setCheckoutLocked(false);
    showReconciliationError([conflict.fee]);
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    console.error(error);
  }
}

function restorePendingFeeSelection() {
  if (feeReconciliationPending || upchargeRefreshPending) return;

  const pendingTitle = getStoredValue(pendingFeeStorageKey);
  if (!pendingTitle) return;

  const availableCheckboxes = [
    ...document.querySelectorAll('.mw-fee--type-cart input[type="checkbox"]:not(:checked)'),
  ];
  const checkbox =
    pendingTitle === '*' && availableCheckboxes.length === 1
      ? availableCheckboxes[0]
      : availableCheckboxes.find((input) => getUpchargeDescriptor(input)?.title === pendingTitle);

  if (!checkbox || checkbox.disabled) return;

  feeReconciliationPending = true;
  setCheckoutLocked(true);
  document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
  checkbox.click();
}

async function changeProductAndRecalculateFee(lineKey, quantity, selectedFee) {
  feeReconciliationPending = true;
  setCheckoutLocked(true);
  document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
  setStoredValue(pendingFeeStorageKey, '*');

  try {
    const cart = await fetchCurrentCart();
    const updates = cart.items.reduce((result, item) => {
      if (item.product_type === feeProductType) result[item.key] = 0;
      return result;
    }, {});

    updates[lineKey] = quantity;
    await changeCart('cart/update.js', { updates }, 'cart-fee:product-quantity-change');
    window.location.reload();
  } catch (error) {
    setStoredValue(pendingFeeStorageKey, '');
    feeReconciliationPending = false;
    setCheckoutLocked(false);
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    console.error(error);
  }
}

function onCartQuantityLinkClick(event) {
  const link = event.target.closest('.cart-page__products line-item-quantity [href*="/cart/change"]');
  const selectedFee = document.querySelector('cart-fee-control[data-fee-title]');

  if (!link || !selectedFee || feeReconciliationPending) return;

  const url = new URL(link.href);
  const lineKey = url.searchParams.get('id');
  const quantity = Number(url.searchParams.get('quantity'));

  if (!lineKey || !Number.isInteger(quantity) || quantity < 0) return;

  event.preventDefault();
  event.stopImmediatePropagation();
  changeProductAndRecalculateFee(lineKey, quantity, selectedFee);
}

function onCartQuantityInputChange(event) {
  const input = event.target.closest('.cart-page__products line-item-quantity [data-line-key]');
  const selectedFee = document.querySelector('cart-fee-control[data-fee-title]');

  if (!input || !selectedFee || feeReconciliationPending) return;

  const lineKey = input.dataset.lineKey;
  const quantity = Number(input.value);

  if (!lineKey || !Number.isInteger(quantity) || quantity < 0) return;

  event.preventDefault();
  event.stopImmediatePropagation();
  changeProductAndRecalculateFee(lineKey, quantity, selectedFee);
}

function delay(milliseconds) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, milliseconds);
  });
}

async function fetchCurrentCart() {
  const cartUrl = new URL(`${window.Shopify.routes.root}cart.js`, window.location.origin);
  cartUrl.searchParams.set('_', Date.now().toString());

  const response = await fetch(cartUrl, {
    cache: 'no-store',
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) throw new Error('Unable to fetch the cart.');

  return response.json();
}

async function refreshAfterUpchargeChange(shouldHaveFee, descriptor) {
  if (upchargeRefreshPending) return;

  upchargeRefreshPending = true;

  try {
    const cart = await waitForFee(descriptor, shouldHaveFee);
    setStoredValue(pendingFeeStorageKey, '');
    dispatchCartChange(cart, 'upcharge-fee:change');
    rememberFeeChange();
    window.location.reload();
  } catch (error) {
    console.error(error);
  } finally {
    upchargeRefreshPending = false;
    feeReconciliationPending = false;
    setCheckoutLocked(false);
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
  }
}

function onUpchargeCheckboxChange(event) {
  if (!event.target.matches('.mw-fee--type-cart input[type="checkbox"], .mw-fee__control--type-checkbox')) return;

  const descriptor = getUpchargeDescriptor(event.target);
  if (!descriptor) return;

  setCheckoutLocked(true);
  document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
  refreshAfterUpchargeChange(event.target.checked, descriptor);
}

function onFeeCardClick(event) {
  const card = event.target.closest('.cart-recap__fee, .cart-recap__order-value > .mw-fee--type-cart');

  if (!card || event.target.closest('input, a, button, select, textarea')) return;

  const checkbox = card.querySelector('input[type="checkbox"]');
  if (!checkbox || checkbox.disabled) return;

  event.preventDefault();
  checkbox.click();
}

function dispatchCartChange(cart, baseEvent) {
  document.documentElement.dispatchEvent(
    new CustomEvent('cart:change', {
      bubbles: true,
      detail: {
        baseEvent,
        cart,
      },
    })
  );
}

async function changeCart(url, body, baseEvent) {
  const response = await fetch(`${window.Shopify.routes.root}${url}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) throw new Error('Unable to update the cart fee.');

  const cart = await response.json();
  dispatchCartChange(cart, baseEvent);

  return cart;
}

class CartFeeControl extends HTMLElement {
  constructor() {
    super();
    this.onChange = this.onChange.bind(this);
  }

  connectedCallback() {
    this.checkbox = this.querySelector('input[type="checkbox"]');
    this.error = this.querySelector('[role="alert"]');

    if (!this.checkbox) return;

    this.checkbox.addEventListener('change', this.onChange);
  }

  disconnectedCallback() {
    this.checkbox?.removeEventListener('change', this.onChange);
  }

  updateState() {
    const selected = this.checkbox.checked;

    this.classList.toggle('cart-recap__fee--selected', selected);
  }

  async onChange() {
    this.updateState();
    if (this.checkbox.checked) return;

    this.checkbox.disabled = true;
    this.setAttribute('aria-busy', 'true');
    this.error.hidden = true;
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    try {
      const currentCart = await fetchCurrentCart();
      const descriptor = { title: normalizeTitle(this.dataset.feeTitle || '') };
      const updates = currentCart.items.reduce((result, item) => {
        if (item.key === this.dataset.lineKey || cartItemMatchesFee(item, descriptor)) result[item.key] = 0;
        return result;
      }, {});

      if (Object.keys(updates).length > 0) {
        await changeCart('cart/update.js', { updates }, 'cart-fee:remove');
      }

      setStoredValue(pendingFeeStorageKey, '');
      rememberFeeChange();
      window.location.reload();
    } catch (error) {
      this.checkbox.checked = true;
      this.updateState();
      this.checkbox.disabled = false;
      this.removeAttribute('aria-busy');
      this.error.hidden = false;
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      console.error(error);
    }
  }
}

class CartOrphanFees extends HTMLElement {
  async connectedCallback() {
    const updates = {};

    this.querySelectorAll('[data-line-key]').forEach((lineItem) => {
      updates[lineItem.dataset.lineKey] = 0;
    });

    if (Object.keys(updates).length === 0) return;

    try {
      const cart = await changeCart('cart/update.js', { updates }, 'cart-fee:remove-orphans');
      const hasFeeItems = cart.items.some((item) => item.product_type === feeProductType);

      if (hasFeeItems) throw new Error('Unable to remove orphaned cart fees.');
    } catch (error) {
      console.error(error);
    }
  }
}

if (!window.customElements.get('cart-fee-control')) {
  window.customElements.define('cart-fee-control', CartFeeControl);
}

if (!window.customElements.get('cart-orphan-fees')) {
  window.customElements.define('cart-orphan-fees', CartOrphanFees);
}

document.addEventListener('change', onUpchargeCheckboxChange, true);
document.addEventListener('change', onCartQuantityInputChange, true);
document.addEventListener('click', onCartQuantityLinkClick, true);
document.addEventListener('click', onFeeCardClick);

formatUpchargeRows();
enhanceUpchargeCards();
normalizeUpchargePrices();
animateFeeChange();

const upchargeObserver = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (node.nodeType !== Node.ELEMENT_NODE) return;

      formatUpchargeRows(node);
      enhanceUpchargeCards(node);
      normalizeUpchargePrices(node);
    });

    normalizeUpchargePrices(mutation.target);
  });
});

upchargeObserver.observe(document.body, { childList: true, subtree: true });
